/**
 * Routes game ServerMessages (from either connection kind) into the game store. Lobby messages
 * (welcome, room, chat, error …) are handled by lobby.ts, which calls this first.
 */
import type { CommandResult } from '@friction/engine';
import type { ServerMessage } from '@friction/protocol';
import { gameStore } from '../game/store';
import { receiveReplayLog, replayStore } from '../replay/store';

type CommandResultListener = (seq: number, result: CommandResult) => void;
const commandListeners = new Set<CommandResultListener>();

/**
 * Subscribe to command acknowledgements, e.g. to flash "order rejected: …" in the HUD.
 * `seq` matches the `cmd` message the store sent. Returns an unsubscribe function.
 */
export function onCommandResult(fn: CommandResultListener): () => void {
  commandListeners.add(fn);
  return () => commandListeners.delete(fn);
}

/** Apply a game message to gameStore. Returns false for messages that are not game messages. */
export function routeGameMessage(msg: ServerMessage): boolean {
  const game = gameStore.getState();
  // While the replay owns the game store, live updates of the finished war (a reconnect's fresh
  // gameStart) are dropped: the parked after-action state comes back when the replay closes.
  if (
    replayStore.getState().active &&
    (msg.t === 'gameStart' || msg.t === 'tick' || msg.t === 'speed' || msg.t === 'spectate')
  )
    return true;
  switch (msg.t) {
    case 'gameStart':
      // A new war, or a rejoin (a finished room resends its replay right after gameStart).
      replayStore.setState({ log: null });
      game.receiveGameStart({
        map: msg.map,
        config: msg.config,
        faction: msg.faction,
        view: msg.view,
        speed: msg.speed,
        paused: msg.paused,
      });
      return true;
    case 'tick':
      game.receiveTick(msg.delta);
      return true;
    case 'speed':
      game.receiveSpeed(msg.speed, msg.paused);
      return true;
    case 'spectate':
      game.receiveSpectate(msg.perspective, msg.view);
      return true;
    case 'replay':
      receiveReplayLog(msg.log);
      return true;
    case 'cmdResult':
      if (!msg.result.ok) console.info(`[net] command ${msg.seq} rejected: ${msg.result.error}`);
      for (const fn of commandListeners) fn(msg.seq, msg.result);
      return true;
    default:
      return false;
  }
}
